/**
 * ChangeLogTab — Super-admin view of every product update across forks.
 *
 * Reads /api/admin/changelog (PIN-gated), supports filter by fork_tag/priority,
 * markdown export, and a "Sync now" button to re-parse PRD.md.
 *
 * PIN is cached in sessionStorage so it survives tab switches but not browser
 * close — a sane balance between convenience and security.
 */
import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Loader2, Lock, Unlock, RefreshCw, Download, ChevronDown, ChevronRight, KeyRound, AlertCircle, GitBranch, Calendar, Clock, Check } from 'lucide-react';

const PIN_KEY = 'docsite_changelog_pin';

export default function ChangeLogTab() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pinRequired, setPinRequired] = useState(false);
  const [pinSet, setPinSet] = useState(false);
  const [pinInput, setPinInput] = useState(sessionStorage.getItem(PIN_KEY) || '');
  const [entries, setEntries] = useState([]);
  const [forkTags, setForkTags] = useState([]);
  const [filterFork, setFilterFork] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [expanded, setExpanded] = useState({});
  const [showPinModal, setShowPinModal] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');

  const checkPinSet = async () => {
    try {
      const r = await api.get('/admin/changelog/pin-status');
      setPinSet(r.data.pin_set);
    } catch {}
  };

  const load = async () => {
    setLoading(true); setError('');
    try {
      const params = { limit: 200 };
      if (filterFork) params.fork_tag = filterFork;
      if (filterPriority) params.priority = filterPriority;
      const headers = pinInput ? { 'X-Changelog-Pin': pinInput } : {};
      const r = await api.get('/admin/changelog', { params, headers });
      setEntries(r.data.entries || []);
      setForkTags(r.data.fork_tags || []);
      setPinRequired(r.data.pin_required);
      if (pinInput && r.data.pin_required) sessionStorage.setItem(PIN_KEY, pinInput);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        setPinRequired(true);
        sessionStorage.removeItem(PIN_KEY);
        setError('PIN required');
      } else {
        setError(e?.response?.data?.detail || 'Could not load change log');
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { checkPinSet(); load(); /* eslint-disable-next-line */ }, [filterFork, filterPriority]);

  const sync = async () => {
    setSyncMsg('Syncing…');
    try {
      const r = await api.post('/admin/changelog/sync');
      setSyncMsg(`Added ${r.data.added}, skipped ${r.data.skipped} (already-known)`);
      await load();
      setTimeout(() => setSyncMsg(''), 4000);
    } catch (e) {
      setSyncMsg(e?.response?.data?.detail || 'Sync failed');
    }
  };

  const downloadMd = async () => {
    try {
      const headers = pinInput ? { 'X-Changelog-Pin': pinInput } : {};
      const r = await api.get('/admin/changelog/export.md', { headers, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/markdown' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `docsite-changelog-${new Date().toISOString().slice(0, 10)}.md`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Download failed');
    }
  };

  if (loading && !entries.length) {
    return <div className="flex items-center gap-2 p-8 text-slate-500"><Loader2 size={16} className="animate-spin" />Loading change log…</div>;
  }

  return (
    <div data-testid="changelog-tab" className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <GitBranch size={18} className="text-sky-600" />Change Log
          </h2>
          <p className="text-sm text-slate-600 mt-1">Every product update, every fork, every date — auto-synced from PRD.md.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setShowPinModal(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg bg-white border border-slate-200 hover:border-slate-400 text-slate-700"
            data-testid="changelog-pin-btn">
            {pinSet ? <Lock size={12} /> : <Unlock size={12} />}
            {pinSet ? 'Change PIN' : 'Set PIN'}
          </button>
          <button onClick={sync}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg bg-sky-50 border border-sky-200 hover:bg-sky-100 text-sky-700"
            data-testid="changelog-sync-btn">
            <RefreshCw size={12} />Sync now
          </button>
          <button onClick={downloadMd}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg bg-slate-900 text-white hover:bg-slate-800"
            data-testid="changelog-download-btn">
            <Download size={12} />Download .md
          </button>
        </div>
      </div>

      {syncMsg && (
        <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 flex items-center gap-1.5">
          <Check size={12} />{syncMsg}
        </div>
      )}

      {/* PIN gate */}
      {pinRequired && !sessionStorage.getItem(PIN_KEY) && (
        <PinGate
          onSubmit={(p) => { setPinInput(p); sessionStorage.setItem(PIN_KEY, p); load(); }}
          error={error}
        />
      )}

      {/* Filter bar */}
      {entries.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-slate-500">Filter:</span>
          <select value={filterFork} onChange={(e) => setFilterFork(e.target.value)}
            className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs"
            data-testid="changelog-fork-filter">
            <option value="">All forks ({forkTags.length})</option>
            {forkTags.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}
            className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs"
            data-testid="changelog-priority-filter">
            <option value="">All priorities</option>
            <option value="P0">P0</option>
            <option value="P1">P1</option>
            <option value="P2">P2</option>
            <option value="P3">P3</option>
          </select>
          <span className="ml-auto text-slate-500">{entries.length} entries</span>
        </div>
      )}

      {/* Empty / error */}
      {error && !pinRequired && (
        <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 flex items-center gap-1.5">
          <AlertCircle size={12} />{error}
        </div>
      )}

      {!entries.length && !loading && !pinRequired && (
        <div className="border border-dashed border-slate-200 rounded-2xl p-10 text-center text-slate-500 text-sm">
          No entries yet. Tap "Sync now" to import from PRD.md.
        </div>
      )}

      {/* Timeline */}
      <div className="space-y-3" data-testid="changelog-timeline">
        {entries.map((e) => {
          const isOpen = expanded[e.id];
          return (
            <div key={e.id} className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={`changelog-entry-${e.id}`}>
              <button onClick={() => setExpanded((x) => ({ ...x, [e.id]: !x[e.id] }))}
                className="w-full flex items-start justify-between gap-3 p-4 hover:bg-slate-50 text-left">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-slate-900 text-white">
                      <GitBranch size={10} />{e.fork_tag}
                    </span>
                    {e.priority && <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${priorityClass(e.priority)}`}>{e.priority}</span>}
                    {e.status && <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${statusClass(e.status)}`}>{e.status}</span>}
                    {e.date_str && (
                      <span className="text-[10px] text-slate-500 inline-flex items-center gap-1">
                        <Calendar size={10} />{e.date_str}
                      </span>
                    )}
                  </div>
                  <p className="font-semibold text-slate-900 text-sm leading-snug">{e.headline}</p>
                  <p className="text-[11px] text-slate-400 mt-1 inline-flex items-center gap-1">
                    <Clock size={10} />Synced {new Date(e.synced_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                  </p>
                </div>
                {isOpen ? <ChevronDown size={16} className="text-slate-400 mt-1 shrink-0" /> : <ChevronRight size={16} className="text-slate-400 mt-1 shrink-0" />}
              </button>
              {isOpen && (
                <div className="px-4 pb-5 pt-0 border-t border-slate-100 bg-slate-50/50">
                  <pre className="whitespace-pre-wrap font-sans text-xs text-slate-700 leading-relaxed mt-3" data-testid={`changelog-body-${e.id}`}>
                    {e.body_md || '(empty)'}
                  </pre>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showPinModal && (
        <PinModal
          pinSet={pinSet}
          onClose={() => setShowPinModal(false)}
          onSaved={async () => { setShowPinModal(false); await checkPinSet(); await load(); }}
        />
      )}
    </div>
  );
}

function priorityClass(p) {
  if (p === 'P0') return 'bg-rose-100 text-rose-800';
  if (p === 'P1') return 'bg-amber-100 text-amber-800';
  if (p === 'P2') return 'bg-sky-100 text-sky-800';
  return 'bg-slate-100 text-slate-700';
}
function statusClass(s) {
  if (s === 'DONE') return 'bg-emerald-100 text-emerald-800';
  if (s === 'IN PROGRESS' || s === 'WIP') return 'bg-amber-100 text-amber-800';
  if (s === 'BLOCKED') return 'bg-rose-100 text-rose-800';
  return 'bg-slate-100 text-slate-700';
}

function PinGate({ onSubmit, error }) {
  const [pin, setPin] = useState('');
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (pin) onSubmit(pin); }}
          className="border border-amber-200 bg-amber-50 rounded-2xl p-6 flex flex-col items-center max-w-md mx-auto" data-testid="changelog-pin-gate">
      <Lock size={24} className="text-amber-600 mb-3" />
      <p className="font-semibold text-amber-900 mb-1">PIN required</p>
      <p className="text-xs text-amber-700 mb-4 text-center">Enter the change-log access PIN to view entries.</p>
      <input type="password" value={pin} onChange={(e) => setPin(e.target.value)}
        className="w-full px-3 py-2 rounded-xl border border-amber-300 bg-white text-sm text-center tracking-widest focus:border-amber-500 outline-none"
        placeholder="••••" maxLength={24} autoFocus data-testid="changelog-pin-input" />
      {error === 'PIN required' && <p className="text-xs text-rose-600 mt-2">PIN incorrect, try again.</p>}
      <button type="submit" disabled={!pin}
        className="mt-3 px-5 py-2 bg-slate-900 text-white rounded-xl text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
        data-testid="changelog-pin-submit">Unlock</button>
    </form>
  );
}

function PinModal({ pinSet, onClose, onSaved }) {
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (newPin.length < 4) { setError('PIN must be at least 4 characters'); return; }
    if (newPin !== confirmPin) { setError('PINs do not match'); return; }
    setSaving(true); setError('');
    try {
      await api.put('/admin/changelog/pin', { new_pin: newPin, current_pin: pinSet ? currentPin : undefined });
      sessionStorage.setItem(PIN_KEY, newPin);
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not set PIN');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4">
      <form onSubmit={save} className="bg-white rounded-2xl max-w-md w-full p-6 space-y-3" data-testid="changelog-pin-modal">
        <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><KeyRound size={16} />{pinSet ? 'Change PIN' : 'Set PIN'}</h3>
        <p className="text-xs text-slate-500">PIN protects the change log — anyone (including future agents) without it can't read or download entries.</p>
        {pinSet && (
          <input type="password" value={currentPin} onChange={(e) => setCurrentPin(e.target.value)} placeholder="Current PIN"
            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" data-testid="changelog-current-pin" required />
        )}
        <input type="password" value={newPin} onChange={(e) => setNewPin(e.target.value)} placeholder="New PIN (min 4 chars)"
          className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" data-testid="changelog-new-pin" required minLength={4} />
        <input type="password" value={confirmPin} onChange={(e) => setConfirmPin(e.target.value)} placeholder="Confirm new PIN"
          className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" data-testid="changelog-confirm-pin" required minLength={4} />
        {error && <p className="text-xs text-rose-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm text-slate-600 rounded-xl hover:bg-slate-100">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 bg-slate-900 text-white rounded-xl text-sm font-medium hover:bg-slate-800 disabled:opacity-50" data-testid="changelog-save-pin">
            {saving ? 'Saving…' : 'Save PIN'}
          </button>
        </div>
      </form>
    </div>
  );
}
